import { useEffect, useState, useMemo } from "react";
import api, { formatMoney } from "@/lib/api";
import { PageHeader, Panel, Pill, EmptyState } from "@/components/Bits";
import { ShoppingBag, BookOpen, GameController, Palette, Funnel } from "@phosphor-icons/react";

const CATEGORY_META = {
  uniform: { icon: ShoppingBag, label: "Uniforms", tone: "terracotta" },
  footwear: { icon: ShoppingBag, label: "Footwear", tone: "terracotta" },
  accessory: { icon: ShoppingBag, label: "Accessories", tone: "mustard" },
  kit: { icon: Palette, label: "Activity Kits", tone: "lavender" },
  book: { icon: BookOpen, label: "Books", tone: "sage" },
  toy: { icon: GameController, label: "Toys", tone: "mustard" },
  stationery: { icon: Palette, label: "Stationery", tone: "lavender" },
};

export default function Apparel() {
  const [catalog, setCatalog] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("catalog");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [c, o] = await Promise.all([api.get("/apparel/catalog"), api.get("/apparel/orders")]);
    setCatalog(c.data); setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const set = new Set(catalog.map(c => c.category));
    return ["all", ...Array.from(set)];
  }, [catalog]);

  const filtered = filter === "all" ? catalog : catalog.filter(c => c.category === filter);

  return (
    <div data-testid="apparel-page">
      <PageHeader eyebrow="Inventory" title="Apparel, Books, Toys & Kits" subtitle="Uniforms, learning materials, activity kits & accessories — order, stitch, deliver." />

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("catalog")} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "catalog" ? "bg-terracotta text-white" : "bg-white border border-border"}`} data-testid="apparel-tab-catalog">Catalog ({catalog.length})</button>
        <button onClick={() => setTab("orders")} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "orders" ? "bg-terracotta text-white" : "bg-white border border-border"}`} data-testid="apparel-tab-orders">Orders ({orders.length})</button>
      </div>

      {tab === "catalog" && (
        <>
          <div className="flex items-center gap-2 mb-5 overflow-x-auto">
            <Funnel size={16} className="text-ink-soft shrink-0" />
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`apparel-filter-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize whitespace-nowrap ${filter === c ? "bg-ink text-white" : "bg-white border border-border"}`}>
                {c === "all" ? "All" : (CATEGORY_META[c]?.label || c)}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? <EmptyState title="No items" /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="apparel-catalog-grid">
              {filtered.slice(0, 48).map(item => {
                const meta = CATEGORY_META[item.category] || { icon: ShoppingBag, label: item.category, tone: "ink" };
                return (
                  <div key={item.item_id} className="bg-white rounded-3xl border border-border p-5 hover:shadow-hoverlift transition">
                    <div className="text-5xl mb-2">{item.image}</div>
                    <div className="font-heading font-bold">{item.name}</div>
                    <Pill tone={meta.tone}>{meta.label}</Pill>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="font-heading text-xl font-bold">{formatMoney(item.price, item.currency)}</div>
                      <Pill tone={item.stock > 20 ? "sage" : "mustard"}>{item.stock} in stock</Pill>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "orders" && (
        <Panel testid="apparel-orders">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-ink-soft text-xs uppercase tracking-wider text-left"><th className="py-2">Order</th><th>Items</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.order_id} className="border-t border-border">
                    <td className="py-3 font-medium">{o.order_id.slice(-6).toUpperCase()}</td>
                    <td className="py-3 max-w-xs truncate">{o.items.map(i => i.name).join(", ")}</td>
                    <td className="py-3 font-medium">{formatMoney(o.amount, o.currency)}</td>
                    <td className="py-3"><Pill tone={o.status === "delivered" ? "sage" : "mustard"}>{o.status.replace("_", " ")}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
